import { useState } from 'react';

import { CalendarIcon } from 'lucide-react';
import type { SelectSingleEventHandler } from 'react-day-picker';

import { Button } from '@/shadcn/button';
import { Calendar } from '@/shadcn/calendar';
import { FakeInput, UnstyledInput } from '@/shadcn/fakeInput';
import { Popover, PopoverContent, PopoverTrigger } from '@/shadcn/popover';

import {
  convertToDate,
  convertToYMD,
  getTodayDateStr
} from '@/components/forms/utils/dateConversionRules';
import { validYMD } from '@/components/forms/utils/dateValidationRules';
import { isLockedWidget } from '@/components/forms/utils/links';
import { useDynamicProps } from '@/hooks/useDynamicProps';
import { usePropsSynchronization } from '@/hooks/usePropsSynchronization';
import { TestId } from '@/types/testId';
import type { WidgetWriterProps } from '../widget.type';

type Props = {
  todayVisible?: boolean;
} & WidgetWriterProps;

export const DateEntry = (rjsfProps: Props) => {
  const onInputChange = (value: string) => {
    // we prevent empty strings from being set as the value and instead set it to undefined
    props.onChange(value ? value : undefined);
  };

  const props = useDynamicProps(rjsfProps);
  usePropsSynchronization(props, rjsfProps, onInputChange);

  // parse the date and check for errors
  const error = !!props.value && !validYMD(props.value);

  // check if the widget is locked because of forms links
  const isLocked = isLockedWidget(props.id, props.formContext?.links);
  const isDisabled = (props.readOnly || isLocked || props.disabled) as boolean;

  // open or close calendar
  const [activeCalendar, setActiveCalendar] = useState(false);

  const onDatePickerChange: SelectSingleEventHandler = newDateStr => {
    props.onChange(convertToYMD(newDateStr));
    return setActiveCalendar(false);
  };

  // reset dates to todays day
  const setToday = () => {
    if (isDisabled) return;
    props.onChange(getTodayDateStr());
  };

  const isTodayVisible = props.todayVisible ?? true;

  return (
    <div className="flex items-center gap-2">
      <FakeInput
        className="flex max-w-[150px] items-center justify-between"
        disabled={isDisabled}
        invalid={error || props.invalid}
      >
        <UnstyledInput
          aria-invalid={error || props.invalid}
          aria-label={props.label}
          className="no-calendar -mr-4 relative w-fit px-0 pl-2 uppercase"
          data-cy={`alt-date-${props.name}`}
          data-testid={TestId.FormWriter.DateEntry.DateInput}
          disabled={isDisabled}
          id={props.id}
          onChange={e => onInputChange(e.target.value)}
          onClick={e => e.preventDefault()}
          readOnly={isDisabled}
          required={props.required}
          type="date"
          value={props.value || ''}
        />
        {!isLocked && (
          <Popover onOpenChange={setActiveCalendar} open={activeCalendar}>
            <PopoverTrigger asChild>
              <Button
                aria-label="Click to open the calendar"
                className="z-2 mr-1 text-muted-foreground hover:text-primary"
                data-cy={`open-calendar-button-${props.name}`}
                data-testid={TestId.FormWriter.DateEntry.CalendarButton}
                disabled={isDisabled}
                onClick={() => setActiveCalendar(!activeCalendar)}
                size="icon"
                variant="ghost"
              >
                <CalendarIcon />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-fit overflow-hidden p-0">
              <Calendar
                captionLayout="dropdown"
                data-cy={`calendar-${props.name}`}
                data-testid={TestId.FormWriter.DateEntry.Calendar}
                disabled={isDisabled}
                endMonth={new Date(2100, 10, 31)}
                initialFocus
                mode="single"
                onSelect={onDatePickerChange}
                required={props.required}
                selected={
                  props.value && !error ? convertToDate(props.value) : undefined
                }
              />
            </PopoverContent>
          </Popover>
        )}
      </FakeInput>

      {!isDisabled && isTodayVisible && (
        <Button
          aria-label="Click to set the date to today"
          className="font-normal text-primary! text-sm normal-case"
          data-cy={`set-today-button-${props.name}`}
          data-testid={TestId.FormWriter.DateEntry.TodayButton}
          disabled={isDisabled}
          onClick={setToday}
          type="button"
          variant="ghost"
        >
          Today
        </Button>
      )}
    </div>
  );
};
